import React, { useState, useCallback, useEffect } from "react";
import { SafetyOutlined, PhoneOutlined } from "@ant-design/icons";
import { Alert, Button, Spin, Typography } from "antd";
import { connect } from "dva";
import { router } from "umi";
import { getSessionStorage, removeSessionStorage, getLocalStorage } from "@/utils/storage";
import { useNeedsToVerifyPhone, NeedsToVerifyPhoneReason } from "@/hooks/useNeedsToVerifyPhone";
import Box, { BoxHeader } from "@/pages/user/components/Box";
import PhoneVerificationForm from "./components/PhoneVerificationForm";
import styles from "./style.less";
const { Text, Title } = Typography;
// Session storage key to track if phone verification is required (non-skippable)
// This key is set during login when MFA is required and phone is not verified
// It is cleared when phone verification is completed or skipped (if allowed)
export const PHONE_VERIFICATION_REQUIRED_KEY = "phoneVerificationRequired";
function PhoneVerification({ dispatch, userId, phoneNumber, isLoadingProfile, currentUser }) {
    const [step, setStep] = useState("prompt");
    const { needsToVerifyPhone, reason } = useNeedsToVerifyPhone({
        roles: currentUser?.roles,
        phoneNumberConfirmed: currentUser?.phoneNumberConfirmed,
        skipPathnameCheck: true,
    });
    // Check if verification is required (non-skippable) from session storage
    const mfaRequired = getSessionStorage(PHONE_VERIFICATION_REQUIRED_KEY) === true;
    // Fetch user profile if not already loaded (needed for userId and phoneNumber)
    // This handles the case where we navigate directly to this page or after hot reload
    useEffect(() => {
        const isLoggedIn = !!getLocalStorage("dataAuth")?.access_token;
        if (!userId && !isLoadingProfile && isLoggedIn) {
            dispatch({ type: "user/fetchCurrentProfile" });
        }
    }, [dispatch, userId, isLoadingProfile]);
    const handleVerifyClick = useCallback(() => {
        setStep("verify");
    }, []);
    const handleSkip = useCallback(() => {
        removeSessionStorage(PHONE_VERIFICATION_REQUIRED_KEY);
        let queryParams = window.location.search;
        if (queryParams.startsWith("?")) {
            queryParams = queryParams.slice(1);
        }
        router.push(`/${queryParams ? `?${queryParams}` : ""}`);
    }, []);
    const handleVerificationComplete = useCallback(() => {
        // update current user profile
        dispatch({ type: "user/fetchCurrentProfile" });
        removeSessionStorage(PHONE_VERIFICATION_REQUIRED_KEY);
        let queryParams = window.location.search;
        if (queryParams.startsWith("?")) {
            queryParams = queryParams.slice(1);
        }
        // If MFA is required, redirect to 2FA challenge setup (PD-2333)
        const newRoute = mfaRequired ? "/user/two-factor" : "/";
        router.push(`${newRoute}${queryParams ? `?${queryParams}` : ""}`);
    }, [mfaRequired, dispatch]);
    const handleBack = useCallback(() => {
        setStep("prompt");
    }, []);
    // Show loading state while fetching profile data
    if (isLoadingProfile || !userId) {
        return (React.createElement(Box, { width: "30%" },
            React.createElement("div", { style: { display: "flex", justifyContent: "center", padding: "40px 0" } },
                React.createElement(Spin, { size: "large" }))));
    }
    // Render the verification form step
    if (step === "verify") {
        return (React.createElement(PhoneVerificationForm, { userId: userId, phoneNumber: phoneNumber, showLogoutButton: reason === NeedsToVerifyPhoneReason.Role, showChangePhoneNumberButton: reason === NeedsToVerifyPhoneReason.Role, onComplete: handleVerificationComplete, onBack: handleBack }));
    }
    return (React.createElement(Box, { width: "30%" },
        React.createElement(BoxHeader, { showLogoutButton: reason === NeedsToVerifyPhoneReason.Role },
            React.createElement("img", { alt: "logo", className: styles.logo, src: "/img/autosled-logo.png" }),
            React.createElement(Title, { level: 3, style: { marginBottom: 8, marginTop: 16 } }, "Secure Your Account"),
            React.createElement(Text, { type: "secondary", style: { textAlign: "center" } }, "Help us keep your account secure by verifying your phone number")),
        React.createElement(Alert, { className: styles.infoAlert, type: "info", icon: React.createElement(SafetyOutlined, null), showIcon: true, message: React.createElement("strong", null, "Why verify your phone?"), description: React.createElement("ul", { className: styles.benefitsList },
                React.createElement("li", null, "Secure your account with two-factor authentication"),
                React.createElement("li", null, "Receive important security alerts"),
                React.createElement("li", null, "Get shipment notifications and updates")) }),
        React.createElement(Button, { type: "primary", icon: React.createElement(PhoneOutlined, null), size: "large", block: true, onClick: handleVerifyClick, className: styles.verifyButton }, "Verify Phone Number"),
        !needsToVerifyPhone && (React.createElement(Button, { type: "link", onClick: handleSkip, className: styles.skipButton }, "I'll do this later"))));
}
export default connect(({ user, loading, }) => ({
    userId: user.currentProfile?.id,
    phoneNumber: user.currentProfile?.phoneNumber,
    isLoadingProfile: loading.effects["user/fetchCurrentProfile"],
    currentUser: user.currentProfile,
}))(PhoneVerification);
